import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react'
import { Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import type { AppChrome } from '@/components/layout/app-chrome'
import { Sidebar } from '@/components/layout/Sidebar'
import { SettingsPanel } from '@/components/settings/SettingsPanel'
import { ExplorePanel } from '@/packages/explore'
import { useUiStore } from '@/stores/ui.store'

/**
 * Signed-in application shell — Figma home frames, menu closed
 * (2256:281962 …) and menu open (2256:282249 …).
 *
 * From 1200px up the open menu is part of the layout and pushes the content
 * (222px panel ↔ 60px rail). Below that it floats above the page as a drawer,
 * exactly as the 900/600/400 "menu open" frames show.
 */
export function AppShell(): ReactElement {
  const { t } = useTranslation()
  const sidebarOpen = useUiStore((state) => state.sidebarOpen)
  const toggleSidebar = useUiStore((state) => state.toggleSidebar)
  const setSidebarOpen = useUiStore((state) => state.setSidebarOpen)

  const [settingsOpen, setSettingsOpen] = useState(false)
  const [exploreOpen, setExploreOpen] = useState(false)

  const closeDrawer = useCallback(() => {
    setSidebarOpen(false)
  }, [setSidebarOpen])

  const chrome = useMemo<AppChrome>(
    () => ({
      openMenu: toggleSidebar,
      openUserPanel: () => {
        setSettingsOpen(true)
      },
      openExplore: () => {
        setExploreOpen(true)
      },
      unreadCount: 9,
    }),
    [toggleSidebar],
  )

  // ⌘K / Ctrl+K toggles Explore — the shortcut the panel's own badge advertises.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key.toLowerCase() !== 'k' || !(event.metaKey || event.ctrlKey)) return
      event.preventDefault()
      setExploreOpen((value) => !value)
    }
    globalThis.addEventListener('keydown', onKeyDown)
    return () => {
      globalThis.removeEventListener('keydown', onKeyDown)
    }
  }, [])

  // Escape closes the floating drawer.
  useEffect(() => {
    if (!sidebarOpen) return
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') closeDrawer()
    }
    globalThis.addEventListener('keydown', onKeyDown)
    return () => {
      globalThis.removeEventListener('keydown', onKeyDown)
    }
  }, [sidebarOpen, closeDrawer])

  useEffect(() => {
    if (exploreOpen) {
      document.body.classList.add('overflow-hidden')
    } else {
      document.body.classList.remove('overflow-hidden')
    }

    return () => {
      document.body.classList.remove('overflow-hidden')
    }
  }, [exploreOpen])

  return (
    <div className="bg-canvas flex min-h-dvh items-start">
      {/* In-flow sidebar, 1200px and up */}
      <div className="sticky top-0 hidden shrink-0 lg:block">
        <Sidebar
          variant={sidebarOpen ? 'panel' : 'rail'}
          onToggle={toggleSidebar}
          onExplore={() => {
            setExploreOpen(true)
          }}
          exploreOpen={exploreOpen}
        />
      </div>

      {/* The header belongs to the page, not the shell: Discover, News and
          Event each fill the header's second row differently. */}
      <div data-name="content" className="flex min-w-px flex-1 flex-col items-start gap-2">
        <main className="w-full">
          <Outlet context={chrome} />
        </main>
      </div>

      {/* Floating drawer below 1200px */}
      {sidebarOpen && (
        <>
          <button
            type="button"
            aria-label={t('nav.closeMenu')}
            onClick={closeDrawer}
            className="fixed inset-0 z-40 cursor-default lg:hidden"
          />
          <div className="fixed inset-y-0 left-0 z-50 lg:hidden">
            <Sidebar
              variant="panel"
              onToggle={closeDrawer}
              onExplore={() => {
                setExploreOpen(true)
              }}
              onNavigate={closeDrawer}
              exploreOpen={exploreOpen}
            />
          </div>
        </>
      )}

      {exploreOpen && (
        <div
          data-name="bg-overlay"
          className="bg-overlay fixed inset-0 z-50 backdrop-blur-[2px]"
          onClick={() => {
            setExploreOpen(false)
          }}
          aria-hidden="true"
        />
      )}
      <SettingsPanel
        open={settingsOpen}
        onClose={() => {
          setSettingsOpen(false)
        }}
      />

      <ExplorePanel
        open={exploreOpen}
        onClose={() => {
          setExploreOpen(false)
        }}
      />
    </div>
  )
}
